// assets/src/api/ajax.js

import { refreshNonce } from './nonce';

/**
 * POST to admin-ajax with automatic one-shot nonce healing.
 *
 * The body carries naluBookingVars.security, which can be dead on a stale /
 * statically-cached page. On a 403 we mint a fresh nonce (see api/nonce.js),
 * swap it into the body, and replay the request exactly once.
 *
 * SAFE TO REPLAY: a nonce 403 is rejected at WP's verify_nonce() gate — the
 * FIRST line of every handler — before anything downstream (portal hold, Stripe
 * charge, confirm) runs, so replaying with a fresh nonce can never double-act.
 * Retry is gated to 403 ONLY; any other failure is returned untouched, so the
 * "no inline retry after a charge" rule for confirm still holds.
 *
 * Accepts FormData or URLSearchParams (both expose has()/set()). Returns the
 * parsed JSON envelope, or { success:false } on a transport/parse failure —
 * matching the fallback every call site already branches on.
 */
export async function postNaluAjax(body) {
  const ajaxUrl = (window.naluBookingVars || {}).ajax_url;

  const doFetch = () => fetch(ajaxUrl, {
    method: 'POST',
    body,
    credentials: 'same-origin',
  });

  let res;
  try {
    res = await doFetch();
  } catch (_) {
    return { success: false };
  }

  // Stale-page nonce → refresh once and replay with the fresh value.
  if (res.status === 403) {
    const fresh = await refreshNonce();
    if (fresh) {
      if (typeof body.set === 'function') {
        if (body.has('security')) body.set('security', fresh);
        if (body.has('nonce')) body.set('nonce', fresh);
      }
      try {
        res = await doFetch();
      } catch (_) {
        return { success: false };
      }
    }
  }

  return res.json().catch(() => ({ success: false }));
}
